import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ClipboardList, CheckCircle2, X, Trash2, Edit2, Undo2, Archive, RotateCcw, Search, Download, ReceiptText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFormTab } from "@/hooks/useFormTab";
import { InvoiceModal } from "@/pages/InvoicePage";
import { BranchFilter } from "@/components/BranchFilter";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";

const ORDER_STATUS_COLOR = {
  "Approved": "text-emerald-600",
  "Not Approved": "text-slate-400",
  "Order Not Received": "text-sky-600",
  "Work Cancelled": "text-rose-600",
  "Not Required": "text-amber-600",
  "Other (Cancel)": "text-violet-600",
};

export default function JMSPage() {
  useUserDirectory();
  const { user, hasPerm } = useAuth();
  const openFormTab = useFormTab();
  const isAdmin = ((user?.role) || "").toLowerCase() === "admin";
  const [tab, setTab] = useState("Ready");
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [completing, setCompleting] = useState(null); // JMS entry being completed
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  async function exportExcel() {
    setExporting(true);
    try {
      const params = {
        status: tab,
        ...(search.trim() && { q: search.trim() }),
        ...(filterBranch && { branch_id: filterBranch }),
      };
      const r = await api.get("/jms/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jms_${tab.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Export failed");
    } finally { setExporting(false); }
  }

  const params = useMemo(() => ({ status: tab }), [tab]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/jms", params);
  const load = reload;

  const filtered = list.filter(j => {
    if (filterBranch && j.branch_id !== filterBranch) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [
      j.estimate_number, j.jms_number, j.client_name, j.project_name, j.branch_name,
      j.service_period, j.jms_date, j.status, j.created_by, j.completed_by, j.done_by,
      // Search on resolved user names too (email stays searchable via the
      // raw fields above).
      formatUser(j.created_by, ""), formatUser(j.completed_by, ""), formatUser(j.done_by, ""),
    ].some(v => (v || "").toString().toLowerCase().includes(q));
  });

  async function deleteJms(id) {
    if (!window.confirm("Delete this JMS Ready entry? Line qty will be returned to the estimate.")) return;
    await api.delete(`/jms/${id}`);
    toast.success("JMS deleted");
    load();
  }

  async function reverseToReady(j) {
    if (!window.confirm(`Reverse JMS #${j.jms_number || j.id.slice(0, 6)} back to Ready? The MS Number and Date will be cleared so you can re-edit the lines and re-complete it.`)) return;
    try {
      await api.post(`/jms/${j.id}/reverse-to-ready`);
      toast.success("JMS reversed to Ready");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reverse failed"); }
  }

  async function markDone(j) {
    if (!window.confirm(`Mark JMS #${j.jms_number || j.id.slice(0, 6)} as DONE? This is a terminal state — only an Admin can restore it back to Completed.`)) return;
    try {
      await api.post(`/jms/${j.id}/mark-done`);
      toast.success("JMS marked Done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark Done"); }
  }

  async function restoreToCompleted(j) {
    if (!window.confirm(`Restore JMS #${j.jms_number || j.id.slice(0, 6)} from Done back to Completed? (Admin only)\n\n• The existing sales invoice / e-invoice will NOT be touched.\n• The estimate quantity consumed by that invoice is released so a corrected invoice can be raised.\n• The old invoice number stays visible on the estimate and this JMS for reference.`)) return;
    try {
      await api.post(`/jms/${j.id}/restore-to-completed`);
      toast.success("JMS restored to Completed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  }

  // Aug-2026 — Invoice Trail: open a raised sales invoice from its JMS card.
  const openInvoice = async (inv) => {
    try {
      const { data } = await api.get(`/sales-invoices/${inv.id}`);
      openFormTab({
        path: `/sales-invoices/view/${inv.id}`,
        label: `Invoice · ${inv.invoice_number}`,
        parent: "/sales-invoices",
        render: ({ close, saved, viewList }) => (
          <InvoiceModal type="sales" invoice={data} onClose={close} onSaved={saved} onViewList={viewList} />
        ),
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open invoice"); }
  };
  const createInvoice = (j) => openFormTab({
    path: `/sales-invoices/new/jms-${j.id}`,
    label: `New Invoice · JMS ${j.jms_number || j.id.slice(0, 6)}`,
    parent: "/sales-invoices",
    onSaved: () => load(),
    render: ({ close, saved, viewList }) => (
      <InvoiceModal type="sales" invoice={null}
        prefilled={{ kind: "FROM_JMS", jms: j }}
        onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });

  return (
    <div className="p-6 max-w-7xl">
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList size={28} className="text-[#F97316]" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">JMS (Joint/Job Measurement Sheet)</h1>
          <div className="text-sm text-slate-500">Pull line items from Approved estimates and mark them measured.</div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {["Ready", "Completed", "Done"].map(t => (
          <button key={t} data-testid={`tab-jms-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === t ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{t}</button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="jms-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Estimate No, JMS No, client, project, branch, service period…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"
          />
          {search && (
            <button type="button" data-testid="jms-search-clear" onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="jms-filter-branch" />
        <button data-testid="jms-export-excel" onClick={exportExcel} disabled={exporting}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-300 font-bold uppercase tracking-wider text-slate-700 hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50">
          <Download size={12} className={exporting ? "animate-bounce" : ""} /> {exporting ? "Exporting…" : "Export Excel"}
        </button>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500" data-testid="jms-count">
          {filtered.length} {tab.toLowerCase()}{search ? ` · filtered by "${search}"` : ""}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="border border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
          {search
            ? <>No {tab} JMS matches &quot;<b>{search}</b>&quot;. Try a different keyword or clear the search.</>
            : <>No {tab} JMS entries yet. Open an Approved estimate and click &quot;Mark Ready for JMS&quot; to create one.</>}
        </div>
      )}

      <div className="space-y-3" data-testid="jms-list">
        {filtered.map(j => {
          const total = (j.lines || []).reduce((s, l) => s + Number(l.amount || 0), 0);
          return (
          <div key={j.id} className="border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-orange-100 text-orange-800">
                    From {j.estimate_number}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${
                    j.status === "Done" ? "bg-slate-200 text-slate-800"
                    : j.status === "Completed" ? "bg-emerald-100 text-emerald-800"
                    : "bg-blue-100 text-blue-800"
                  }`}>{j.status}</span>
                  {j.jms_number && <span className="text-[11px] text-slate-700 font-bold">JMS #{j.jms_number}</span>}
                  {j.jms_date && <span className="text-[11px] text-slate-500">· {j.jms_date}</span>}
                </div>
                <div className="text-sm text-slate-700">
                  <span className="font-semibold">{j.client_name || "—"}</span>
                  <span className="text-slate-400 mx-2">·</span>
                  <span>{j.project_name || "—"}</span>
                  <span className="text-slate-400 mx-2">·</span>
                  <span>{j.branch_name || "—"}</span>
                </div>
                {(j.service_period || j.client_poc) && (
                  <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`jms-meta-${j.id}`}>
                    {j.service_period && <>Service Period: {j.service_period}</>}
                    {j.service_period && j.client_poc && <span className="text-slate-300 mx-1.5">·</span>}
                    {j.client_poc && <>Client POC: <span className="font-semibold text-slate-700">{j.client_poc}</span></>}
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mt-1">Created by <UserLabel value={j.created_by} fallback="—" /> on {new Date(j.created_at).toLocaleString()}</div>
                {j.status === "Done" && (j.done_by || j.done_via) && (
                  <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`jms-done-info-${j.id}`}>
                    Done{j.done_by ? <> by <UserLabel value={j.done_by} fallback="—" /></> : null}
                    {j.done_at ? ` on ${new Date(j.done_at).toLocaleString()}` : ""}
                    {j.done_via ? <span className="text-emerald-700 font-semibold"> · {j.done_via}</span> : null}
                  </div>
                )}
                {(j.invoices || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`jms-invoice-trail-${j.id}`}>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Invoices:</span>
                    {j.invoices.map(inv => (
                      <button key={inv.id} data-testid={`jms-invoice-link-${inv.id}`} onClick={() => openInvoice(inv)}
                        title={`Open ${inv.invoice_number} (${inv.status || "—"})`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-500 hover:bg-sky-100 font-semibold">
                        <ReceiptText size={10} /> {inv.invoice_number}
                        <span className="text-sky-600 font-bold">{formatINR(inv.total_amount || 0)}</span>
                        {inv.invoice_date && <span className="text-sky-500 font-normal">· {inv.invoice_date}</span>}
                      </button>
                    ))}
                    {j.invoices.length > 1 && (
                      <span className="text-[11px] text-slate-600 font-bold" data-testid={`jms-invoiced-total-${j.id}`}>
                        = {formatINR(j.invoiced_total || 0)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {j.status === "Ready" && (
                  <>
                    <button data-testid={`edit-jms-${j.id}`} onClick={() => setEditing(j)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 hover:border-[#F97316] hover:text-[#F97316]"><Edit2 size={12} /> Edit Lines</button>
                    <button data-testid={`complete-jms-${j.id}`} onClick={() => setCompleting(j)} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5"><CheckCircle2 size={12} /> Mark Completed</button>
                    <button onClick={() => deleteJms(j.id)} className="inline-flex items-center text-rose-600 hover:text-rose-800 p-1.5"><Trash2 size={13} /></button>
                  </>
                )}
                {j.status === "Completed" && (
                  <>
                    {hasPerm("sales_invoices", "create") && (
                      <button data-testid={`create-invoice-jms-${j.id}`} onClick={() => createInvoice(j)}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5"
                        title="Open a New Sales Invoice with this JMS's estimate pulled and the JMS number pre-filled">
                        <ReceiptText size={12} /> Create Invoice
                      </button>
                    )}
                    <button data-testid={`reverse-jms-${j.id}`} onClick={() => reverseToReady(j)}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 hover:border-[#F97316] hover:text-[#F97316]"
                      title="Move this JMS back to Ready so you can re-edit lines and re-complete it">
                      <Undo2 size={12} /> Reverse to Ready
                    </button>
                    <button data-testid={`mark-done-jms-${j.id}`} onClick={() => markDone(j)}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-slate-700 hover:bg-slate-900 text-white px-3 py-1.5"
                      title="Terminal state — only Admin can restore this back to Completed">
                      <Archive size={12} /> Mark as Done
                    </button>
                  </>
                )}
                {j.status === "Done" && isAdmin && (
                  <button data-testid={`restore-jms-${j.id}`} onClick={() => restoreToCompleted(j)}
                    className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5"
                    title="Admin: restore this Done JMS back to Completed so it can be edited again">
                    <RotateCcw size={12} /> Restore to Completed
                  </button>
                )}
                {j.status === "Done" && !isAdmin && (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 self-center px-3 py-1.5 border border-dashed border-slate-300">
                    Terminal — Admin only
                  </span>
                )}
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {[
                    { h: "Item Code", cls: "text-left" },
                    { h: "Order No", cls: "text-left" },
                    { h: "Order Month", cls: "text-left" },
                    { h: "Server", cls: "text-left" },
                    { h: "Description", cls: "text-left" },
                    { h: "Qty", cls: "text-right" },
                    { h: "Unit", cls: "text-left" },
                    { h: "Rate", cls: "text-right" },
                    { h: "Amount", cls: "text-right" },
                  ].map(({ h, cls }) =>
                    <th key={h} className={`${cls} py-1.5 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500`}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(j.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5 px-2 text-slate-800">{l.item_code || "—"}</td>
                    <td className="py-1.5 px-2 whitespace-nowrap">
                      {l.order_number || <span className="text-slate-300">—</span>}
                      <span className={`ml-1 text-[9px] ${ORDER_STATUS_COLOR[l.order_status] || "text-slate-400"}`}>
                        ({l.order_status || "Not Approved"})
                      </span>
                    </td>
                    <td className="py-1.5 px-2 whitespace-nowrap">{l.order_month || <span className="text-slate-300">—</span>}</td>
                    <td className="py-1.5 px-2">{l.client_server || <span className="text-slate-300">—</span>}</td>
                    <td className="py-1.5 px-2">{l.description}</td>
                    <td className="py-1.5 px-2 text-right tabular">{l.quantity}</td>
                    <td className="py-1.5 px-2 text-slate-500">{l.unit}</td>
                    <td className="py-1.5 px-2 text-right tabular">{formatINR(l.rate)}</td>
                    <td className="py-1.5 px-2 text-right tabular font-semibold">{formatINR(l.amount)}</td>
                  </tr>
                ))}
                <tr><td colSpan={8} className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</td>
                  <td className="py-2 px-2 text-right tabular font-bold text-[#F97316]">{formatINR(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>);
        })}
      </div>

      <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />

      {completing && <CompleteModal entry={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); load(); }} />}
      {editing && <EditLinesModal entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CompleteModal({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({
    jms_number: entry.jms_number || "",
    jms_date: entry.jms_date || new Date().toISOString().slice(0, 10),
    service_period: entry.service_period || "",
  });
  async function save() {
    if (!form.jms_number || !form.jms_date) { toast.error("JMS Number and Date are required"); return; }
    try {
      await api.post(`/jms/${entry.id}/complete`, form);
      toast.success("JMS marked Completed — estimate's Service Period updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete JMS");
    }
  }
  return (
    <ViewOverlay testid="complete-jms-modal">
      <div className="bg-white min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={22} className="text-emerald-600" />
            <div>
              <h2 className="font-display text-2xl font-bold">Mark JMS Completed</h2>
              <div className="text-[11px] text-slate-500 mt-0.5">
                From estimate <b>{entry.estimate_number}</b> · {entry.client_name} · {entry.project_name}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
            <X size={14} /> Close
          </button>
        </div>
        <div className="px-8 py-6 max-w-3xl mx-auto space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">JMS Number *</div>
            <input data-testid="jms-number-input" value={form.jms_number} onChange={e => setForm({ ...form, jms_number: e.target.value })} placeholder="Free text — e.g. JMS/2026/0042" className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">JMS Date *</div>
            <input data-testid="jms-date-input" type="date" value={form.jms_date} onChange={e => setForm({ ...form, jms_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Service Period (overwrites estimate)</div>
            <input data-testid="jms-service-period-input" value={form.service_period} onChange={e => setForm({ ...form, service_period: e.target.value })} placeholder="e.g. Apr 2026 – Jun 2026" className="w-full px-3 py-2 border border-slate-300 text-sm focus:border-[#F97316] focus:outline-none" />
          </label>
          <div className="text-[11px] text-slate-600 bg-orange-50 border border-orange-200 px-3 py-2">
            Saving will overwrite estimate <b>{entry.estimate_number}</b>&apos;s Service Period.
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button onClick={onClose} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:bg-slate-50">Cancel</button>
            <button data-testid="save-jms-complete" onClick={save} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white">Mark Completed</button>
          </div>
        </div>
      </div>
    </ViewOverlay>
  );
}

function EditLinesModal({ entry, onClose, onSaved }) {
  const [lines, setLines] = useState(JSON.parse(JSON.stringify(entry.lines || [])));
  async function save() {
    try {
      await api.put(`/jms/${entry.id}`, { lines });
      toast.success("JMS lines updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }
  return (
    <ViewOverlay testid="edit-jms-modal">
      <div className="bg-white min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div className="flex items-center gap-3">
            <Edit2 size={20} className="text-[#F97316]" />
            <div>
              <h2 className="font-display text-2xl font-bold">Edit JMS Lines</h2>
              <div className="text-[11px] text-slate-500 mt-0.5">
                From estimate <b>{entry.estimate_number}</b> · {entry.client_name} · {entry.project_name} · Adjust pulled quantity. Validation against remaining qty (excluding this record) runs on save.
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
            <X size={14} /> Close
          </button>
        </div>
        <div className="px-8 py-6 max-w-[1400px] mx-auto">
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-32">Item Code</th>
                  <th className="text-left py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500">Description</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-32">Qty</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-28">Rate</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-32">Amount</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2.5 px-3 text-slate-700 font-semibold">{l.item_code || "—"}</td>
                    <td className="py-2.5 px-3">{l.description}</td>
                    <td className="py-2.5 px-3"><input type="number" step="0.01" value={l.quantity} onChange={e => { const v = [...lines]; v[i].quantity = Number(e.target.value || 0); v[i].amount = Number((v[i].quantity * v[i].rate).toFixed(2)); setLines(v); }} className="w-full px-2 py-1.5 border border-slate-300 text-right text-xs focus:border-[#F97316] focus:outline-none" /></td>
                    <td className="py-2.5 px-3 text-right tabular text-slate-700">{formatINR(l.rate)}</td>
                    <td className="py-2.5 px-3 text-right tabular font-bold">{formatINR(l.amount)}</td>
                    <td className="py-2.5 px-3 text-center">
                      <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-rose-600 hover:text-rose-800" title="Remove line"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-slate-400 text-xs">All lines removed. Cancel to keep this JMS unchanged, or save to clear it.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-6 border-t border-slate-200 mt-6">
            <button onClick={onClose} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:bg-slate-50">Cancel</button>
            <button data-testid="save-jms-edit" onClick={save} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] text-white">Save Changes</button>
          </div>
        </div>
      </div>
    </ViewOverlay>
  );
}
